// Main-thread client voor de jaaraggregaat-Web Worker.
//
// Gebruik:
//   const job = runYearAggregateInWorker(scenario, { referenceYear });
//   job.onProgress(p => ...);
//   const result = await job.promise;     // throwt bij error
//   job.cancel();                         // optioneel
//
// We instantieeren een nieuwe Worker per run; bij cancel termineren we 'm
// hard. Workers zijn licht en hergebruik is voor onze use-case niet nodig.

import type { Scenario } from "../types/scenario";
import type { YearAggregateResult } from "./yearAggregate";
import type { WorkerOutgoing } from "./worker";

export interface YearAggregateJob {
  promise: Promise<YearAggregateResult>;
  onProgress: (cb: (pct: number, phase: string) => void) => void;
  cancel: () => void;
}

interface RunOptions {
  referenceYear: number;
  resolution?: number;
}

export function runYearAggregateInWorker(
  scenario: Scenario,
  options: RunOptions,
): YearAggregateJob {
  const worker = new Worker(new URL("./worker.ts", import.meta.url), {
    type: "module",
  });

  const progressListeners: Array<(pct: number, phase: string) => void> = [];
  let cancelled = false;

  const promise = new Promise<YearAggregateResult>((resolve, reject) => {
    worker.onmessage = (e: MessageEvent<WorkerOutgoing>) => {
      const msg = e.data;
      if (msg.type === "progress") {
        for (const cb of progressListeners) cb(msg.pct, msg.phase);
      } else if (msg.type === "done") {
        worker.terminate();
        resolve(msg.result);
      } else if (msg.type === "error") {
        worker.terminate();
        reject(new Error(msg.message));
      }
    };
    worker.onerror = (e) => {
      if (cancelled) return;
      worker.terminate();
      reject(new Error(e.message));
    };
    worker.postMessage({
      type: "compute",
      scenario,
      referenceYear: options.referenceYear,
      resolution: options.resolution,
    });
  });

  return {
    promise,
    onProgress: (cb) => {
      progressListeners.push(cb);
    },
    cancel: () => {
      cancelled = true;
      worker.terminate();
    },
  };
}
